<template>
  <div class="rm-screen">
    <div v-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!groups.length" class="rm-empty">
      <template v-if="search">{{ $t("report_mobile.no_results_for", { search }) }}</template>
      <template v-else>
        <v-icon size="40" color="#8a817a">mdi-food-off</v-icon>
        {{ $t("no_taken_meals_data") }}
      </template>
    </div>

    <template v-else>
      <!-- zbir: preuzeto / poručeno sa napretkom (bez preuzetog kad firma ne sme da vidi preuzimanje) -->
      <div v-if="showTaken" class="rm-summary">
        <div class="rm-summary__row">
          <div class="rm-summary__labels">
            <span>{{ title }}</span>
            <span>{{ summaryText }}</span>
          </div>
          <span class="rm-summary__value">
            {{ summary.taken }}<span class="rm-summary__value-of"> / {{ summary.ordered }}</span>
          </span>
        </div>
        <div
          class="rm-progress rm-progress--inverse"
          role="progressbar"
          :aria-valuenow="summary.taken"
          aria-valuemin="0"
          :aria-valuemax="summary.ordered"
        >
          <div class="rm-progress__fill" :style="{ width: `${summary.percent}%` }"></div>
        </div>
      </div>
      <div v-else class="rm-stats rm-stats--pair">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("report_desktop.total_meals") }}</span>
          <span class="rm-stat__value rm-num">{{ summary.ordered }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("meals") }}</span>
          <span class="rm-stat__value rm-num">{{ mealCount }}</span>
        </div>
      </div>

      <section v-for="group in groups" :key="group.key" class="rm-card rm-mcard">
        <template v-if="group.name">
          <button
            type="button"
            class="rm-mcard__head"
            :aria-expanded="String(isOpen(group.key))"
            @click="toggle(group.key)"
          >
            <span class="rm-mcard__title">
              <span class="rm-mcard__name">{{ group.name }}</span>
              <span class="rm-muted">{{ pluralT("report_mobile.meals_n", group.ordered) }}</span>
            </span>
            <span v-if="showTaken" class="rm-mcard__value rm-num">
              {{ group.taken }}<span class="rm-mcard__of"> / {{ group.ordered }}</span>
            </span>
            <v-icon size="18" color="#8a817a" class="rm-mcard__chevron" :class="{ 'is-open': isOpen(group.key) }">
              mdi-chevron-down
            </v-icon>
          </button>
          <div
            v-if="showTaken"
            class="rm-progress rm-mcard__progress"
            :class="{ 'rm-progress--complete': group.complete }"
            role="progressbar"
            :aria-valuenow="group.taken"
            aria-valuemin="0"
            :aria-valuemax="group.ordered"
          >
            <div class="rm-progress__fill" :style="{ width: `${group.percent}%` }"></div>
          </div>
        </template>

        <ul v-if="!group.name || isOpen(group.key)" class="rm-list" :class="{ 'rm-mcard__list': group.name }">
          <li v-for="meal in group.meals" :key="meal.key" class="rm-mrow">
            <div class="rm-mrow__main">
              <span class="rm-mrow__name">{{ meal.name }}</span>
              <span class="rm-mrow__value rm-num" :class="{ 'is-complete': showTaken && meal.complete }">
                <v-icon v-if="showTaken && meal.complete" size="16" color="#15803d">mdi-check-circle</v-icon>
                <template v-if="showTaken">
                  {{ meal.taken }}<span class="rm-mrow__of"> / {{ meal.total }}</span>
                </template>
                <template v-else>{{ meal.total }}</template>
              </span>
            </div>
            <div v-if="meal.shifts.length" class="rm-mrow__shifts">
              <span v-for="shift in meal.shifts" :key="shift.id" class="rm-mrow__shift rm-num">
                <report-shift-tag :shift="shift.id"></report-shift-tag>
                <template v-if="showTaken">{{ shift.taken }} / {{ shift.orders }}</template>
                <template v-else>{{ shift.orders }}</template>
              </span>
            </div>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import hasPlural from "@/components/report/shared/hasPlural";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { percentOf } from "@/components/report/shared/takenMath";
import "@/components/report/mobile/screens/reportMobileMealCards.scss";

/**
 * Spisak obroka sa brojem preuzetih / poručenih, po grupama (firmama) ili bez grupe (grupa bez naziva): zbir sa
 * napretkom gore, pa kartice sa redovima obroka i smenama. Samo prikazuje; podatke, pretragu i izvoz daje
 * ekran koji ga koristi (Preuzeti obroci, Preuzeto).
 */
export default {
  name: "CompanyMobileTakenList",
  mixins: [hasPlural],
  components: { ReportShiftTag },
  props: {
    loading: { type: Boolean, default: false },
    /** [{ key, name, ordered, taken, meals: [{ key, name, total, taken, shifts: [{ id, orders, taken }] }] }] */
    groups: { type: Array, default: () => [] },
    title: { type: String, default: "" },
    search: { type: String, default: "" },
    /** false = firma ne vidi preuzimanje: samo poručene količine */
    showTaken: { type: Boolean, default: true },
  },
  data: () => ({
    closed: {},
  }),
  computed: {
    summary() {
      const taken = this.groups.reduce((sum, group) => sum + group.taken, 0);
      const ordered = this.groups.reduce((sum, group) => sum + group.ordered, 0);
      return { taken, ordered, percent: percentOf(taken, ordered) };
    },
    mealCount() {
      return this.groups.reduce((sum, group) => sum + group.meals.length, 0);
    },
    summaryText() {
      const named = this.groups.filter((group) => group.name).length;
      const parts = named > 1 ? [this.pluralT("report_mobile.companies_n", named)] : [];
      parts.push(this.pluralT("report_mobile.meals_n", this.summary.ordered));
      return parts.join(" · ");
    },
  },
  methods: {
    isOpen(key) {
      return Boolean(this.search) || !this.closed[key];
    },
    toggle(key) {
      this.closed = { ...this.closed, [key]: !this.closed[key] };
    },
  },
};
</script>
